// A panel's PLAN-VIEW FOOTPRINT — the polygon the backend extrudes to get
// real, shape-aware absorption instead of a bounding box.
//
// Every panel this product can place is a vertical prism over a 2D polygon:
// a flat panel is a rectangle, a corner trap is the right-isoceles triangle
// `cornerWedgeVertices` defines, and a tapered panel is the convex quad the
// "Customize dimensions" editor produces. `cornerPrismGeometry` and
// `taperedPanelGeometry` (@roomtreatment/viewport-3d) already build their
// meshes exactly this way; this is the same shape, in the form the solver
// needs.
//
// Distinct from ./footprint's `footprintHalfXZ`, which is the rotated
// bounding-box HALF-EXTENTS used for clamping a panel inside the room. That
// answers "how much room does this reach into"; this answers "what exactly
// does it cover".
//
// CONVENTION — get this backwards and every side-face normal inverts:
//   * panel-local metres in the (x, z) plane; x = width, z = room-facing.
//   * wound CCW, i.e. POSITIVE 2D shoelace signed area.
//   * under that winding, edge (ex, ez) has outward normal (ez, -ex), which
//     makes the front edge's normal come out as local +Z — matching the
//     "+Z is room-facing" convention panel-taper and cornerWedgeVertices
//     both already document.
// contracts/panel-footprint-examples.json pins all of it across languages.

import type { PanelEntry } from './editorState';

/** A footprint vertex, panel-local metres: `[x, z]`. */
export type FootprintPoint = [number, number];

/** The corner trap's plan-view triangle: a right angle at the local origin
 *  (which `poseToTransform` seats exactly on the room's corner point) and
 *  two legs of length `leg` (the panel's `size.w`) running at ±45° from
 *  local +Z to a hypotenuse at `Z = leg/√2`. Local +Z is the wedge's
 *  room-facing normal — the corner bisector, once yawed into place — so the
 *  two legs land exactly on the two real wall planes: each wall's own
 *  inward normal is ±45° from the bisector for a 90° corner, matching where
 *  these legs actually are. A symmetric box-in-X-Z triangle (legs on the
 *  local axes) is flush only if local +Z pointed at ONE wall's normal, not
 *  the bisector between two — that mismatch was the original bug.
 *
 *  Lives here rather than in @roomtreatment/panel-surface (which re-exports
 *  it, so every existing import still resolves) because it is pure math with
 *  no THREE dependency, and both the wire layer and the 3D layer need it:
 *  `panelToWire` in @roomtreatment/api-client must derive a footprint, and
 *  api-client deliberately depends on `core` alone. Routing it through
 *  panel-surface would have dragged `three` into the serializer.
 *
 *  Returned CW; `panelFootprint` reverses it to satisfy the CCW convention. */
export function cornerWedgeVertices(
  leg: number,
  interiorAngle: number = Math.PI / 2,
): [number, number][] {
  const h = interiorAngle / 2;
  const s = leg * Math.sin(h);
  const c = leg * Math.cos(h);
  return [[0, 0], [-s, c], [s, c]];
}

/** Twice-the-area shoelace, halved: positive for CCW, negative for CW, zero
 *  for a degenerate polygon. Also the panel's true plan-view area, which is
 *  the number the whole shape-awareness effort exists to get right. */
export function footprintSignedArea(poly: readonly number[][]): number {
  if (poly.length < 3) return 0;
  let acc = 0;
  for (let i = 0; i < poly.length; i++) {
    const [x1, z1] = poly[i];
    const [x2, z2] = poly[(i + 1) % poly.length];
    acc += x1 * z2 - x2 * z1;
  }
  return acc / 2;
}

function allFinite(values: readonly number[]): boolean {
  return values.every((v) => Number.isFinite(v));
}

/** Force CCW winding, so callers never have to care how a source ordered
 *  its vertices. `cornerWedgeVertices` happens to be CW. */
function toCCW(poly: FootprintPoint[]): FootprintPoint[] {
  return footprintSignedArea(poly) < 0 ? [...poly].reverse() : poly;
}

/** Rotate the (already CCW) cycle to start at the lowest-z, then lowest-x
 *  vertex.
 *
 *  Winding fixes the DIRECTION around the polygon but not where the loop
 *  starts, so reversing a CW source yields a correct-but-rotated cycle.
 *  That is invisible to the physics (a sum over faces doesn't care about
 *  order) but very visible to the cross-language contract: Python has to
 *  emit the same array, element for element, or the golden fixture can
 *  only ever be compared up to rotation — which would quietly stop
 *  catching a genuine vertex-order bug. Canonicalizing costs nothing and
 *  keeps the fixture a real equality check.
 *
 *  The anchor falls out sensibly for every shape in use: the corner
 *  wedge starts on its right-angle vertex (the point `poseToTransform`
 *  seats on the room corner), and a rectangle or taper starts at its
 *  back-left corner. */
function canonicalStart(poly: FootprintPoint[]): FootprintPoint[] {
  let best = 0;
  for (let i = 1; i < poly.length; i++) {
    const [x, z] = poly[i];
    const [bx, bz] = poly[best];
    if (z < bz || (z === bz && x < bx)) best = i;
  }
  return best === 0 ? poly : [...poly.slice(best), ...poly.slice(0, best)];
}

/** The plain centred rectangle a panel of this size implies — the shape a
 *  null footprint stands in for, and the fallback whenever a taper is
 *  unusable. */
export function rectFootprint(w: number, d: number): FootprintPoint[] {
  const hw = w / 2;
  const hd = d / 2;
  return [[-hw, -hd], [hw, -hd], [hw, hd], [-hw, hd]];
}

/** How close a taper has to sit to the plain rectangle before it counts as
 *  no taper at all. Matches @roomtreatment/panel-taper's `isTaperTrivial`
 *  default so the two agree on what "untapered" means. */
const TRIVIAL_EPS_M = 1e-4;

/**
 * The panel's footprint polygon, or `null` when the panel is just a plain
 * centred `size.w × size.d` rectangle.
 *
 * `null` is the common case and is deliberate: it keeps a plain box's wire
 * payload byte-identical to what it has always been, and lets the backend
 * take its exact pre-existing box path rather than a numerically-almost-
 * identical polygon path. A taper that has collapsed back to a rectangle
 * normalizes to `null` for the same reason.
 */
export function panelFootprint(
  panel: PanelEntry,
  interiorAngle?: number,
): FootprintPoint[] | null {
  const { w, d } = panel.size;
  if (!allFinite([w, d]) || w <= 0 || d <= 0) return null;

  if (panel.shape === 'corner') {
    // The leg length IS size.w — the same reading `cornerWedgeVertices`
    // gets everywhere else it's called. size.d is the material thickness
    // and plays no part in the plan-view shape, which is exactly why the
    // box model under-reports a corner trap's area by 3x.
    // Included angle defaults to π/2 (catalog 90° trap); pass the seated
    // corner's interior for non-rectangular rooms.
    const wedge = cornerWedgeVertices(w, interiorAngle ?? Math.PI / 2);
    const flat = wedge.flatMap(([x, z]) => [x, z]);
    if (!allFinite(flat)) return null;
    const poly = canonicalStart(toCCW(wedge.map(([x, z]) => [x, z] as FootprintPoint)));
    return footprintSignedArea(poly) > 0 ? poly : null;
  }

  const t = panel.taper;
  if (!t) return null;

  const flat = [t.bl.x, t.bl.z, t.br.x, t.br.z, t.fr.x, t.fr.z, t.fl.x, t.fl.z];
  // A corrupted document must not inject NaN into the solver's quadrature:
  // one NaN sample poisons the entire per-mode dissipation sum, which is a
  // far worse failure than solving the bounding box.
  if (!allFinite(flat)) return null;

  // bl -> br -> fr -> fl is CCW for any non-self-intersecting taper, which
  // panel-taper's mutual clamp guarantees.
  const poly: FootprintPoint[] = [
    [t.bl.x, t.bl.z], [t.br.x, t.br.z], [t.fr.x, t.fr.z], [t.fl.x, t.fl.z],
  ];

  if (isTrivialFootprint(poly, w, d)) return null;
  const area = footprintSignedArea(poly);
  if (!(area > 0)) return null;   // degenerate or inverted — fall back to the box
  return canonicalStart(poly);
}

/** True when the polygon is, within `eps`, the plain centred rectangle of
 *  `w × d` — vertex for vertex, in order. */
function isTrivialFootprint(
  poly: readonly FootprintPoint[], w: number, d: number, eps = TRIVIAL_EPS_M,
): boolean {
  const rect = rectFootprint(w, d);
  if (poly.length !== rect.length) return false;
  return poly.every(([x, z], i) =>
    Math.abs(x - rect[i][0]) < eps && Math.abs(z - rect[i][1]) < eps);
}
